/**
 * The three codes a Max signup can arrive with. Pure functions, no React.
 *
 * They are never conflated, and keeping them apart is the whole point of this
 * module existing instead of one "code" field:
 *
 *   ?promo=   a Stripe coupon. Changes PRICE. Lives on the subscription.
 *   ?ref=     a partner/reseller tag. Changes ATTRIBUTION only, never price.
 *             ?partner= and ?aff= are accepted aliases.
 *   ?trial=   a Max trial code. Changes ENTITLEMENT. Never price, never
 *             attribution.
 *
 * A reseller link carries all three, because the partner hands out the
 * campaign discount, wants credit for the signup, and may have a trial code
 * from sales:
 *
 *   /signup?promo=MAXLAUNCH-26&ref=RESELLER-ACME&trial=MAX-B7K9-QR3T
 *
 * NORMALIZATION is the same rule max-trial-service.normalizeCode applies on
 * the way into the database, and the same rule the Family portal applies:
 * trim, upper case, at most 64 characters, only A-Z 0-9 _ and -. A value that
 * fails it is treated as NO CODE AT ALL rather than as an error, because a
 * mistyped affiliate tag must never cost a customer their signup.
 *
 * What a code is WORTH never comes from here. GET /api/billing/public/max-offer
 * answers from Stripe and from the trial-code registry; nothing in this file
 * computes a discount or an entitlement.
 */

/** Query keys that may carry each code. The first one that normalizes wins. */
// Only ?promo= here. The Family portal accepts ?code= as an alias, but in
// THIS portal ?code= already means two other things: the OAuth authorization
// code on /auth/callback and the 6 digit signup verification code. A
// six-digit value normalizes cleanly, so accepting the alias would let a
// resend-email or a misconfigured OAuth redirect be captured as a promo code
// and advertised to the customer as a discount.
export const PROMO_PARAMS = ['promo'] as const;
export const REFERRAL_PARAMS = ['ref', 'partner', 'aff'] as const;
export const TRIAL_PARAMS = ['trial', 'trial_code'] as const;

/** Every key the wizard consumes, so one pass can strip them all. */
export const ALL_CODE_PARAMS: readonly string[] = [
  ...PROMO_PARAMS,
  ...REFERRAL_PARAMS,
  ...TRIAL_PARAMS,
];

export const CODE_PATTERN = /^[A-Z0-9_-]{1,64}$/;

/** The three codes as the wizard holds them. '' means "no code", never null. */
export type ArrivalCodes = {
  promoCode: string;
  referralCode: string;
  trialCode: string;
};

export const NO_CODES: ArrivalCodes = Object.freeze({
  promoCode: '',
  referralCode: '',
  trialCode: '',
});

/** Normalized, or null when the value is not usable as a code. */
export function normalizeCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  if (!code || code.length > 64) return null;
  return CODE_PATTERN.test(code) ? code : null;
}

/**
 * What a customer is allowed to leave in a code field while typing: upper
 * case, allowed characters only, capped. Keeps the field and the stored code
 * in step keystroke by keystroke instead of rejecting on submit.
 */
export function sanitizeTypedCode(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return raw.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 64);
}

function firstCode(search: string | undefined, keys: readonly string[]): string | null {
  const params = new URLSearchParams(search || '');
  for (const key of keys) {
    const code = normalizeCode(params.get(key));
    if (code) return code;
  }
  return null;
}

export function readPromoFromSearch(search?: string): string | null {
  return firstCode(search, PROMO_PARAMS);
}

export function readReferralFromSearch(search?: string): string | null {
  return firstCode(search, REFERRAL_PARAMS);
}

export function readTrialFromSearch(search?: string): string | null {
  return firstCode(search, TRIAL_PARAMS);
}

/** All three codes on a query string, each '' when absent or unusable. */
export function readCodesFromSearch(search?: string): ArrivalCodes {
  return {
    promoCode: readPromoFromSearch(search) || '',
    referralCode: readReferralFromSearch(search) || '',
    trialCode: readTrialFromSearch(search) || '',
  };
}

/** True when any code key is present, even if its value is junk. */
export function hasCodeParams(search?: string): boolean {
  const params = new URLSearchParams(search || '');
  return ALL_CODE_PARAMS.some((key) => params.has(key));
}

/**
 * The query string without the code keys, everything else untouched. utm_*,
 * fbclid, gclid and the portal's own return params stay, because stripping
 * those would break the campaign reporting the codes exist to serve.
 *
 * Returns '' when nothing is left, else '?a=b' ready for navigate().
 */
export function stripCodeParams(search?: string): string {
  const params = new URLSearchParams(search || '');
  ALL_CODE_PARAMS.forEach((key) => params.delete(key));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Last touch wins, per code, independently.
 *
 * A non-empty arrival replaces whatever was stored. An absent arrival never
 * clears a stored code, so a customer who refreshes mid-wizard (or comes back
 * from the Google hop with a bare query string) keeps the codes they arrived
 * with. Same rule as the Family portal's mergeArrivalPromo.
 *
 * Per code, not per link: a prospect who follows a reseller's link and then a
 * campaign ad keeps the reseller's ref and picks up the ad's promo, which is
 * what both parties were promised.
 *
 * This is the WIZARD's rule, not the database's. privacypal-api writes a
 * referral code once per company and refuses to move it afterwards, so
 * whichever ref is in state when the account is created is the one that
 * sticks.
 */
export function mergeArrivalCodes(stored: Partial<ArrivalCodes> | null, arrived: Partial<ArrivalCodes> | null): ArrivalCodes {
  const pick = (key: keyof ArrivalCodes): string => {
    const fresh = normalizeCode(arrived?.[key]);
    if (fresh) return fresh;
    const kept = stored?.[key];
    return typeof kept === 'string' ? kept : '';
  };
  return {
    promoCode: pick('promoCode'),
    referralCode: pick('referralCode'),
    trialCode: pick('trialCode'),
  };
}

/** True when there is nothing worth persisting or sending. */
export function isEmptyCodes(codes: Partial<ArrivalCodes> | null | undefined): boolean {
  if (!codes) return true;
  return !codes.promoCode && !codes.referralCode && !codes.trialCode;
}

/** Cents as a plain dollar string. Whole dollars drop the ".00". */
export function formatCents(cents: unknown): string {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return n % 100 === 0 ? `$${n / 100}` : `$${(n / 100).toFixed(2)}`;
}
